import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { updateCacheTags } from '../../store/slices/cache-tag-slice.js';
import { portalApi } from '../portal/portalApi';
import { basicHeaders, basicRehydration } from '../utils.js';
import { alertConfigurationsApi } from './alertConfigurationsApi.js';
import { clientOwnersApi } from './clientOwnersApi.js';
import { clientsApi } from './clientsApi.js';
import { contactApi } from './contactApi.js';
import { customerGroupsApi } from './customerGroupsApi.js';
import { facilitiesApi } from './facilitiesApi.js';
import { fleetsApi } from './fleetsApi.js';
import { goalsApi } from './goalsApi.js';
import { miscApi } from './miscApi.js';
import { motivePowerApi } from './motivePowerApi.js';
import { newGoalsApi } from './newGoalsApi.js';
import { newTeamsApi } from './newTeamsApi.js';
import { notesApi } from './notesApi.js';
import { powerLoggersApi } from './powerLoggersApi.js';
import { BuilderType, projectsCacheTags } from './projectsApiHelpers.js';
import { projectStatsApi } from './projectStatsApi.js';
import { resourcesApi } from './resourcesApi.js';
import { scheduleGroupsApi } from './scheduleGroupsApi.js';
import { schedulesApi } from './schedulesApi.js';
import { serviceProjectsApi } from './serviceProjectsApi.js';
import { structuresApi } from './structuresApi.js';
import { tagsApi } from './tagsApi.js';
import { taskDataApi } from './taskDataApi.js';
import { tasksApi } from './tasksApi.js';
import { teamsApi } from './teamApi.js';
import { thirdPartyApi } from './thirdPartyApi.js';
import { vehicleApi } from './vehicleApi.js';
import { viewsApi } from './viewsApi.js';
import { portalCacheTags } from '../portal/portalApiHelpers.js';
import { userConfigsApi } from './userConfigsApi.js';
import { surveysApi } from './surveysApi.js';
import { unexpectedAssetsApi } from './unexpectedAssetsApi.js';
import { channelTypesApi } from './channelTypesApi.js';
import { customerChannelPlacementsApi } from './customerChannelPlacementsApi.js';

const portalTags = Object.values(portalCacheTags);
const projectsTags = Object.values(projectsCacheTags);

export const projectsApi = createApi({
  reducerPath: 'projectsApi',

  // prefer caching APIs on the server side
  extractRehydrationInfo: basicRehydration('projectsApi', [
    // "getClients",
    // "getFleets",
    // "getNewTeams",
    // "getAlertConfigurations",
  ]),

  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_APP_PROJECTS_SERVICE}/`,
    prepareHeaders: basicHeaders,
  }),

  tagTypes: [...projectsTags, ...portalTags],

  endpoints: (builder: BuilderType) => ({
    getCacheTags: builder.query<any, void>({
      query: () => `service/cache-tag`,
      providesTags: [projectsCacheTags.CLIENTS],

      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const { data } = await queryFulfilled;
        const { cacheTags } = getState();
        const diffKeys = Object.keys(data).filter((key) => data[key] !== cacheTags?.[key]);
        if (diffKeys.length) {
          console.log('Invalidating cache tags', diffKeys);
          // Convert string keys to proper tag objects for invalidation
          const tagObjects = diffKeys.map((tagType) => ({ type: tagType }));
          dispatch(projectsApi.util.invalidateTags(tagObjects));
          dispatch(portalApi.util.invalidateTags(tagObjects));
        }
        dispatch(updateCacheTags(data));
      },
    }),
    ...clientsApi(builder),
    ...alertConfigurationsApi(builder),
    ...clientOwnersApi(builder),
    ...contactApi(builder),
    ...userConfigsApi(builder),
    ...customerGroupsApi(builder),
    ...facilitiesApi(builder),
    ...fleetsApi(builder),
    ...goalsApi(builder),
    ...miscApi(builder),
    ...motivePowerApi(builder),
    ...newGoalsApi(builder),
    ...newTeamsApi(builder),
    ...notesApi(builder),
    ...powerLoggersApi(builder),
    ...serviceProjectsApi(builder),
    ...projectStatsApi(builder),
    ...resourcesApi(builder),
    ...scheduleGroupsApi(builder),
    ...schedulesApi(builder),
    ...structuresApi(builder),
    ...tagsApi(builder),
    ...taskDataApi(builder),
    ...tasksApi(builder),
    ...teamsApi(builder),
    ...thirdPartyApi(builder),
    ...unexpectedAssetsApi(builder),
    ...vehicleApi(builder),
    ...viewsApi(builder),
    ...surveysApi(builder),
    ...channelTypesApi(builder),
    ...customerChannelPlacementsApi(builder),
  }),
});

export const {
  // clientsApi
  useGetClientsQuery,
  useGetClientQuery,
  useLazyGetClientQuery,
  useGetClientByPortalIdQuery,
  useLazyGetClientByPortalIdQuery,
  useGetClientParentsQuery,
  useGetClientChildrenQuery,
  useGetClientHomeAccountQuery,
  useGetDealersQuery,

  // facilitiesApi
  useUpdateFacilityMutation,

  // clientOwnersApi
  useCreateClientOwnerMutation,
  useUpdateClientOwnerMutation,

  // alertConfigurationsApi
  useGetAlertConfigurationsQuery,
  useCreateAlertConfigurationMutation,
  useUpdateAlertConfigurationMutation,

  // newTeamsApi
  useGetNewTeamsQuery,
  useGetNewTeamClientProjectsQuery,
  useLazyGetNewTeamClientProjectsQuery,

  // userConfigsApi
  useGetUserConfigsQuery,
  useLazyGetUserConfigsQuery,

  // customerGroupsApi
  useGetCustomerGroupsQuery,

  // fleetsApi
  useGetFleetsQuery,
  useGetFleetQuery,
  useLazyGetFleetQuery,
  useGetZonesQuery,
  useCreateZoneMutation,
  useUpdateZoneMutation,
  useDeleteZoneMutation,
  useCreateAssociationTemplateMutation,
  useCreateAssociationMutation,
  useUpdateAssociationMutation,
  useCreateAssetTemplateMutation,
  useUpdateAssetTemplateMutation,
  useUnexpectedAssetsByFleetIdsQuery,
  useRetireFleetAssetMutation,
  useUpdateFleetAssetMutation,

  // motivePowerApi
  useGetMotivePowerQuery,
  useGetBatteriesQuery,
  useGetChargersQuery,

  // powerLoggersApi
  useGetPowerLoggersQuery,
  useGetPowerLoggerQuery,
  useGetPowerLoggerCurrentAssignmentsQuery,
  useGetPowerLoggerAssignmentsQuery,
  useLazyGetPowerLoggerAssignmentsQuery,
  useGetPowerLoggerStatsQuery,
  useGetPowerLoggerCurrentAssignmentQuery,

  // serviceProjectsApi
  useGetProjectQuery,
  useGetProjectsStatsQuery,
  useGetProjectsWithGeoPositionQuery,
  useGetProjectNumberByClientIdsQuery,
  useGetMonitorProjectsSummaryQuery,
  useGetMeasureModelSummaryQuery,
  useSearchProjectsQuery,
  useLazySearchProjectsQuery,
  useLazyGetProjectQuery,
  useUpdateProjectMutation,
  useUpdateProjectEmailReportPreferencesMutation,

  // projectStatsApi
  useGetTopClientsQuery,

  // tagsApi
  useGetTagsQuery,

  // viewsApi
  useGetUserViewsQuery,

  // vehicleApi
  useGetVehiclesQuery,

  // surveysApi
  useGetSurveysQuery,

  // channelTypesApi
  useGetChannelTypesQuery,
  useCreateChannelTypeMutation,
  useUpdateChannelTypeMutation,
  useDeleteChannelTypeMutation,
  useLazyGetChannelTypesQuery,
  useGetCustomerChannelOptionsQuery,
  useCreateCustomerChannelPlacementMutation,

  // mutations
  useLazyGetAMAssetBySerialQuery,
  useSendPasswordResetEmailMutation,
  useClearAMUnexpectedAssetListMutation,
  useIgnoreAMAssetListMutation,
  useUnIgnoreAMAssetListMutation,
  useUpdateSurveyMutation,
  useUpdateBatterySerialMutation,
  useUpdateBatteryLocationMutation,
} = projectsApi;
